import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { formatDateTime, STAGE_DISPLAY_NAMES } from '../lib/utils';
import { 
  Plus, 
  Search, 
  Filter,
  User,
  Building2,
  Calendar,
  Loader2,
  Eye,
  Edit2,
  Trash2,
  Briefcase,
  MoreHorizontal,
  UserPlus,
  Tag,
  Clock,
  ExternalLink,
  Mail,
  Send,
  X as XIcon,
  Users as UsersIcon,
  SlidersHorizontal
} from 'lucide-react';
import {
  getInitials,
  getAvatarColor,
  getRelationshipStatus,
  formatLastActive,
} from '../lib/contactDisplay';
import MassEmailComposer from '../components/email/MassEmailComposer';
import OrganizationCombobox from '../components/OrganizationCombobox';

const CONTACT_TYPE_TAGS = [
  "Decision Maker", "Influencer", "Champion", 
  "Finance", "Operations", "HR", "Other"
];

const CONSENT_STATUSES = [
  { id: 'unknown', label: 'Unknown', color: '#6B7280' },
  { id: 'opt_in', label: 'Opt-in', color: '#10B981' },
  { id: 'opt_out', label: 'Opt-out', color: '#EF4444' },
];

// Helper to determine follow-up urgency
const getFollowUpStatus = (dateStr) => {
  if (!dateStr) return { status: 'none', color: 'text-muted-foreground', bg: '' };
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const followUp = new Date(dateStr);
  followUp.setHours(0, 0, 0, 0);
  
  const diffDays = Math.floor((followUp - today) / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) return { status: 'overdue', color: 'text-red-600', bg: 'bg-red-50' };
  if (diffDays === 0) return { status: 'today', color: 'text-yellow-600', bg: 'bg-yellow-50' };
  return { status: 'future', color: 'text-muted-foreground', bg: '' };
};

export default function Contacts() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAdmin } = useAuth();
  const { get, post, put, del, loading } = useApi();
  
  const [contacts, setContacts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [quickOppDialogOpen, setQuickOppDialogOpen] = useState(false);
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [bulkActionType, setBulkActionType] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [quickOppContact, setQuickOppContact] = useState(null);
  const [bulkComposerOpen, setBulkComposerOpen] = useState(false);
  const [singleComposer, setSingleComposer] = useState(null); // a contact object
  
  // Bulk action form data
  const [bulkOppValue, setBulkOppValue] = useState(0);
  const [bulkOwnerId, setBulkOwnerId] = useState('');
  const [bulkTag, setBulkTag] = useState('');
  const [bulkFollowUp, setBulkFollowUp] = useState('');
  
  // Quick opportunity form
  const [quickOppValue, setQuickOppValue] = useState(0);
  const [quickOppType, setQuickOppType] = useState('Hotel');
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [accountFilter, setAccountFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [consentFilter, setConsentFilter] = useState('all');
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    account_id: '',
    account_name: '',
    phone: '',
    title: '',
    contact_type_tags: [],
    consent_status: 'unknown',
    notes: '',
    next_follow_up_date: '',
  });

  const fetchContacts = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (accountFilter !== 'all') params.append('account_id', accountFilter);
      if (ownerFilter !== 'all') params.append('owner_id', ownerFilter);
      if (tagFilter !== 'all') params.append('contact_type', tagFilter);
      if (consentFilter !== 'all') params.append('consent_status', consentFilter);
      
      const data = await get(`/contacts?${params.toString()}`);
      setContacts(data);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    }
  };

  const fetchAccounts = async () => {
    try {
      const data = await get('/accounts');
      setAccounts(data);
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await get('/users');
      setUsers(data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  useEffect(() => {
    fetchContacts();
    fetchAccounts();
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchContacts();
  }, [searchQuery, accountFilter, ownerFilter, tagFilter, consentFilter]);

  // Handle query params for pre-filling create dialog
  useEffect(() => {
    if (searchParams.get('create') === 'true') {
      const accountId = searchParams.get('account_id');
      const accountName = searchParams.get('account_name');
      
      setFormData(prev => ({
        ...prev,
        account_id: accountId || '',
        account_name: accountName || '',
      }));
      
      setCreateDialogOpen(true);
      // Clear the query params
      setSearchParams({});
    }
  }, [searchParams]);

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      account_id: '',
      account_name: '',
      phone: '',
      title: '',
      contact_type_tags: [],
      consent_status: 'unknown',
      notes: '',
      next_follow_up_date: '',
    });
    setSelectedContact(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedContact) {
        await put(`/contacts/${selectedContact.id}`, formData);
        toast.success('Contact updated');
      } else {
        await post('/contacts', formData);
        toast.success('Contact created');
      }
      setCreateDialogOpen(false);
      resetForm();
      fetchContacts();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEdit = (contact) => {
    setSelectedContact(contact);
    setFormData({
      first_name: contact.first_name,
      last_name: contact.last_name,
      email: contact.email,
      account_id: contact.account_id || '',
      account_name: contact.account_name || '',
      phone: contact.phone || '',
      title: contact.title || '',
      contact_type_tags: contact.contact_type_tags || [],
      consent_status: contact.consent_status || 'unknown',
      notes: contact.notes || '',
      next_follow_up_date: contact.next_follow_up_date || '',
    });
    setCreateDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    try {
      await del(`/contacts/${id}`);
      toast.success('Contact deleted');
      fetchContacts();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const toggleContactSelection = (contactId) => {
    setSelectedContacts(prev => 
      prev.includes(contactId) 
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const toggleAllContacts = () => {
    if (selectedContacts.length === contacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(contacts.map(c => c.id));
    }
  };

  const toggleTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      contact_type_tags: prev.contact_type_tags.includes(tag)
        ? prev.contact_type_tags.filter(t => t !== tag)
        : [...prev.contact_type_tags, tag]
    }));
  };

  // Quick create opportunity from contact
  const handleQuickCreateOpp = async (contact) => {
    setQuickOppContact(contact);
    setQuickOppValue(0);
    setQuickOppType('Hotel');
    setQuickOppDialogOpen(true);
  };

  const submitQuickOpp = async () => {
    if (!quickOppContact) return;
    
    try {
      const result = await post(`/contacts/${quickOppContact.id}/create-opportunity`, {
        deal_value: quickOppValue,
        property_type: quickOppType
      });
      toast.success('Opportunity created in Prospecting stage');
      setQuickOppDialogOpen(false);
      setQuickOppContact(null);
      fetchContacts();
      
      // Optional: navigate to pipeline
      // navigate('/pipeline');
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Bulk actions
  const openBulkAction = (actionType) => {
    if (selectedContacts.length === 0) {
      toast.error('Select contacts first');
      return;
    }
    setBulkActionType(actionType);
    setBulkActionDialogOpen(true);
  };

  const handleBulkAction = async () => {
    try {
      let endpoint = '';
      let body = { contact_ids: selectedContacts };
      
      switch (bulkActionType) {
        case 'create-opportunity':
          endpoint = '/contacts/bulk/create-opportunities';
          body.deal_value = bulkOppValue;
          body.property_type = 'Hotel';
          break;
        case 'assign-owner':
          endpoint = '/contacts/bulk/assign-owner';
          body.owner_id = bulkOwnerId;
          break;
        case 'add-tag':
          endpoint = '/contacts/bulk/add-tag';
          body.tag = bulkTag;
          break;
        case 'update-followup':
          endpoint = '/contacts/bulk/update-followup';
          body.next_follow_up_date = bulkFollowUp;
          break;
        default:
          return;
      }
      
      const result = await post(endpoint, body);
      toast.success(result.message);
      setBulkActionDialogOpen(false);
      setSelectedContacts([]);
      fetchContacts();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getConsentBadge = (status) => {
    const consent = CONSENT_STATUSES.find(c => c.id === status) || CONSENT_STATUSES[0];
    return (
      <Badge 
        className="rounded-sm text-xs"
        style={{ backgroundColor: consent.color, color: '#fff' }}
      >
        {consent.label}
      </Badge>
    );
  };

  const canEdit = (contact) => {
    if (isAdmin) return true;
    return contact.owner_id === user?.id;
  };

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in" data-testid="contacts-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 pb-6 border-b border-border">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
              Relationship Management
            </p>
            <h1 className="font-serif text-3xl sm:text-4xl">
              Contacts
              {!loading && (
                <span className="ml-2 text-2xl sm:text-3xl text-muted-foreground" data-testid="contacts-count">
                  ({contacts.length})
                </span>
              )}
            </h1>
            <p className="text-muted-foreground mt-2">
              Manage your client and prospect contacts
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            {selectedContacts.length > 0 && (
              <>
                <Badge variant="outline" className="rounded-sm border-[#B89B5E] text-[#B89B5E]">
                  {selectedContacts.length} selected
                </Badge>
                
                {/* Bulk Actions Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="rounded-sm" data-testid="bulk-actions-btn">
                      <MoreHorizontal className="h-4 w-4 mr-2" />
                      Bulk Actions
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => openBulkAction('create-opportunity')}>
                      <Briefcase className="h-4 w-4 mr-2" />
                      Create Opportunities
                    </DropdownMenuItem>
                    {isAdmin && (
                      <DropdownMenuItem onClick={() => openBulkAction('assign-owner')}>
                        <UserPlus className="h-4 w-4 mr-2" />
                        Assign Owner
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => openBulkAction('add-tag')}>
                      <Tag className="h-4 w-4 mr-2" />
                      Add Tag
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => openBulkAction('update-followup')}>
                      <Clock className="h-4 w-4 mr-2" />
                      Set Follow-Up Date
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
            
            <Dialog open={createDialogOpen} onOpenChange={(open) => {
              setCreateDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="rounded-sm uppercase tracking-wider text-xs" data-testid="add-contact-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Contact
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="contact-dialog">
                <DialogHeader>
                  <DialogTitle className="text-xl font-semibold">
                    {selectedContact ? 'Edit Contact' : 'Add New Contact'}
                  </DialogTitle>
                </DialogHeader>
                
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">First Name *</Label>
                      <Input
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        required
                        className="rounded-sm"
                        data-testid="contact-first-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Last Name *</Label>
                      <Input
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        required
                        className="rounded-sm"
                        data-testid="contact-last-name"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="rounded-sm"
                      data-testid="contact-email"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Organization</Label>
                    <OrganizationCombobox
                      value={formData.account_id}
                      valueName={formData.account_name}
                      onChange={(id, name) =>
                        setFormData({ ...formData, account_id: id, account_name: name })
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      Optional — leave as "No organization" if this contact isn't tied to one.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Phone</Label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="rounded-sm"
                        data-testid="contact-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Title/Role</Label>
                      <Input
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="e.g., General Manager"
                        className="rounded-sm"
                        data-testid="contact-title"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Contact Type Tags</Label>
                    <div className="flex flex-wrap gap-2">
                      {CONTACT_TYPE_TAGS.map((tag) => (
                        <Badge
                          key={tag}
                          variant={formData.contact_type_tags.includes(tag) ? "default" : "outline"}
                          className="cursor-pointer rounded-sm"
                          onClick={() => toggleTag(tag)}
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Consent Status</Label>
                    <Select
                      value={formData.consent_status}
                      onValueChange={(value) => setFormData({ ...formData, consent_status: value })}
                    >
                      <SelectTrigger className="rounded-sm" data-testid="contact-consent">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CONSENT_STATUSES.map((status) => (
                          <SelectItem key={status.id} value={status.id}>
                            {status.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Next Follow-Up Date</Label>
                    <Input
                      type="date"
                      value={formData.next_follow_up_date}
                      onChange={(e) => setFormData({ ...formData, next_follow_up_date: e.target.value })}
                      className="rounded-sm"
                      data-testid="contact-followup"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Notes</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Internal notes about this contact..."
                      rows={3}
                      className="rounded-sm resize-none"
                      data-testid="contact-notes"
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button type="button" variant="outline" onClick={() => {
                      setCreateDialogOpen(false);
                      resetForm();
                    }} className="rounded-sm">
                      Cancel
                    </Button>
                    <Button type="submit" className="rounded-sm" disabled={loading} data-testid="save-contact-btn">
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (selectedContact ? 'Update' : 'Create')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, or account..."
              className="pl-10 rounded-sm"
              data-testid="contact-search"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Select value={accountFilter} onValueChange={setAccountFilter}>
              <SelectTrigger className="w-[160px] rounded-sm">
                <Building2 className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Account" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Accounts</SelectItem>
                {accounts.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {isAdmin && (
              <Select value={ownerFilter} onValueChange={setOwnerFilter}>
                <SelectTrigger className="w-[150px] rounded-sm">
                  <User className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Owner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Owners</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            <Select value={tagFilter} onValueChange={setTagFilter}>
              <SelectTrigger className="w-[150px] rounded-sm">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {CONTACT_TYPE_TAGS.map((tag) => (
                  <SelectItem key={tag} value={tag}>
                    {tag}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={consentFilter} onValueChange={setConsentFilter}>
              <SelectTrigger className="w-[130px] rounded-sm">
                <SelectValue placeholder="Consent" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {CONSENT_STATUSES.map((status) => (
                  <SelectItem key={status.id} value={status.id}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Contacts Table */}
        <Card className="rounded-sm border">
          <CardContent className="p-0">
            {contacts.length === 0 ? (
              <div className="py-20 text-center" data-testid="contacts-empty-state">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-muted/40 mb-5">
                  <UsersIcon className="h-9 w-9 text-muted-foreground" />
                </div>
                <h3 className="font-serif text-2xl mb-2">
                  {searchQuery || accountFilter !== 'all' || tagFilter !== 'all'
                    ? 'No contacts match your filters'
                    : 'No contacts yet'}
                </h3>
                <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
                  {searchQuery || accountFilter !== 'all' || tagFilter !== 'all'
                    ? 'Try adjusting your search or clearing filters to see more results.'
                    : 'Start building your CRM relationships by adding your first contact.'}
                </p>
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  {(searchQuery || accountFilter !== 'all' || tagFilter !== 'all') && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSearchQuery('');
                        setAccountFilter('all');
                        setTagFilter('all');
                      }}
                      className="rounded-sm"
                      data-testid="empty-state-clear-filters"
                    >
                      <SlidersHorizontal className="h-4 w-4 mr-2" />
                      Clear filters
                    </Button>
                  )}
                  <Button
                    onClick={() => setCreateDialogOpen(true)}
                    className="rounded-sm"
                    data-testid="empty-state-add-contact"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Contact
                  </Button>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="w-10 py-3 px-4">
                        <Checkbox 
                          checked={selectedContacts.length === contacts.length && contacts.length > 0}
                          onCheckedChange={toggleAllContacts}
                        />
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Name</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Account</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Active Stage</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Type</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Consent</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Follow-Up</th>
                      <th className="text-right py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((contact) => {
                      const followUpStatus = getFollowUpStatus(contact.next_follow_up_date);
                      const fullName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.email || 'Contact';
                      const initials = getInitials(contact.first_name, contact.last_name, contact.email);
                      const avatar = getAvatarColor(fullName);
                      const status = getRelationshipStatus(contact.relationship_status || 'unknown');
                      const isSelected = selectedContacts.includes(contact.id);
                      return (
                        <tr 
                          key={contact.id} 
                          className={`group border-b last:border-0 hover:bg-muted/20 transition-colors cursor-pointer ${followUpStatus.bg} ${isSelected ? 'bg-[#B89B5E]/5' : ''}`}
                          onClick={() => navigate(`/contacts/${contact.id}`)}
                          data-testid={`contact-row-${contact.id}`}
                        >
                          <td className="py-3 px-3 sm:py-4 sm:px-4 w-12" onClick={(e) => e.stopPropagation()}>
                            {/* Avatar (default) ↔ checkbox (on hover or when any selected) */}
                            <div className="relative w-9 h-9">
                              <div
                                className={`absolute inset-0 rounded-full flex items-center justify-center text-xs font-semibold transition-opacity ${selectedContacts.length > 0 ? 'opacity-0' : 'opacity-100 group-hover:opacity-0'}`}
                                style={{ backgroundColor: avatar.bg, color: avatar.fg }}
                                data-testid={`contact-avatar-${contact.id}`}
                                title={fullName}
                              >
                                {initials}
                              </div>
                              <div className={`absolute inset-0 flex items-center justify-center transition-opacity ${selectedContacts.length > 0 ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => toggleContactSelection(contact.id)}
                                  data-testid={`contact-checkbox-${contact.id}`}
                                />
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-3 sm:py-4 sm:px-4">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium text-sm truncate">
                                  {contact.first_name} {contact.last_name}
                                </p>
                                {/* Status dot + label */}
                                <span
                                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-medium px-1.5 py-0.5 rounded-full"
                                  style={{ color: status.dot, backgroundColor: `${status.dot}1A` }}
                                  data-testid={`contact-status-${contact.id}`}
                                  title={`Relationship: ${status.label}`}
                                >
                                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: status.dot }} />
                                  {status.label}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground truncate">
                                {contact.title || contact.email || '—'}
                              </p>
                              <p className="text-[10px] text-muted-foreground/80 mt-0.5 flex items-center gap-1">
                                <Clock className="h-2.5 w-2.5" />
                                Last active: {formatLastActive(contact.last_activity_date || contact.last_contacted_date)}
                              </p>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <span className="text-sm">{contact.account_name}</span>
                          </td>
                          <td className="py-4 px-4">
                            {contact.active_stage ? (
                              <div className="flex items-center gap-1">
                                <Badge variant="outline" className="text-xs rounded-sm">
                                  {STAGE_DISPLAY_NAMES[contact.active_stage] || contact.active_stage}
                                </Badge>
                                {contact.active_opportunity_id && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-5 w-5 p-0"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/opportunity/${contact.active_opportunity_id}`);
                                    }}
                                  >
                                    <ExternalLink className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">No Active Opp</span>
                            )}
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex flex-wrap gap-1">
                              {contact.contact_type_tags?.slice(0, 2).map((tag) => (
                                <Badge key={tag} variant="outline" className="text-xs rounded-sm">
                                  {tag}
                                </Badge>
                              ))}
                              {contact.contact_type_tags?.length > 2 && (
                                <Badge variant="outline" className="text-xs rounded-sm">
                                  +{contact.contact_type_tags.length - 2}
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            {getConsentBadge(contact.consent_status)}
                          </td>
                          <td className="py-4 px-4">
                            <span className={`text-sm font-medium ${followUpStatus.color}`}>
                              {contact.next_follow_up_date 
                                ? formatDateTime(contact.next_follow_up_date).split(',')[0] 
                                : '-'}
                              {followUpStatus.status === 'overdue' && ' (Overdue)'}
                              {followUpStatus.status === 'today' && ' (Today)'}
                            </span>
                          </td>
                          <td className="py-3 px-3 sm:py-4 sm:px-4" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-0.5 opacity-100 sm:opacity-60 sm:group-hover:opacity-100 transition-opacity">
                              {/* Quick Email — only enabled if email exists */}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  if (!contact.email) {
                                    toast.error('No email on file for this contact');
                                    return;
                                  }
                                  setSingleComposer(contact);
                                }}
                                className="rounded-sm h-8 w-8 p-0"
                                title={contact.email ? `Email ${contact.email}` : 'No email on file'}
                                data-testid={`quick-email-${contact.id}`}
                              >
                                <Mail className="h-3.5 w-3.5" />
                              </Button>
                              {/* Quick Create Opportunity */}
                              {!contact.active_stage && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleQuickCreateOpp(contact)}
                                  className="rounded-sm h-8 w-8 p-0"
                                  title="Create Opportunity"
                                  data-testid={`quick-opp-${contact.id}`}
                                >
                                  <Briefcase className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/contacts/${contact.id}`)}
                                className="rounded-sm h-8 w-8 p-0"
                                title="View"
                              >
                                <Eye className="h-3.5 w-3.5" />
                              </Button>
                              {canEdit(contact) && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEdit(contact)}
                                    className="rounded-sm h-8 w-8 p-0"
                                    title="Edit"
                                  >
                                    <Edit2 className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDelete(contact.id)}
                                    className="rounded-sm h-8 w-8 p-0 text-destructive hover:text-destructive"
                                    title="Delete"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Create Opportunity Dialog */}
        <Dialog open={quickOppDialogOpen} onOpenChange={setQuickOppDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold">Create Opportunity</DialogTitle>
            </DialogHeader>
            {quickOppContact && (
              <div className="space-y-4 mt-4">
                <div className="p-3 bg-muted/30 rounded-sm">
                  <p className="text-sm font-medium">{quickOppContact.first_name} {quickOppContact.last_name}</p>
                  <p className="text-xs text-muted-foreground">{quickOppContact.account_name}</p>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Deal Value ($)</Label>
                  <Input
                    type="number"
                    value={quickOppValue}
                    onChange={(e) => setQuickOppValue(parseFloat(e.target.value) || 0)}
                    className="rounded-sm"
                    placeholder="0"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Property Type</Label>
                  <Select value={quickOppType} onValueChange={setQuickOppType}>
                    <SelectTrigger className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Hotel">Hotel</SelectItem>
                      <SelectItem value="Resort">Resort</SelectItem>
                      <SelectItem value="Restaurant">Restaurant</SelectItem>
                      <SelectItem value="Spa">Spa</SelectItem>
                      <SelectItem value="Club">Club</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <p className="text-xs text-muted-foreground">
                  Opportunity will be created in <strong>Prospecting</strong> stage
                </p>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={() => setQuickOppDialogOpen(false)} className="rounded-sm">
                    Cancel
                  </Button>
                  <Button onClick={submitQuickOpp} className="rounded-sm" disabled={loading}>
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                      <>
                        <Briefcase className="h-4 w-4 mr-2" />
                        Create
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Bulk Action Dialog */}
        <Dialog open={bulkActionDialogOpen} onOpenChange={setBulkActionDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold">
                {bulkActionType === 'create-opportunity' && 'Create Opportunities'}
                {bulkActionType === 'assign-owner' && 'Assign Owner'}
                {bulkActionType === 'add-tag' && 'Add Tag'}
                {bulkActionType === 'update-followup' && 'Set Follow-Up Date'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-sm text-muted-foreground">
                This will affect {selectedContacts.length} selected contacts
              </p>
              
              {bulkActionType === 'create-opportunity' && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Deal Value ($)</Label>
                  <Input
                    type="number"
                    value={bulkOppValue}
                    onChange={(e) => setBulkOppValue(parseFloat(e.target.value) || 0)}
                    className="rounded-sm"
                    placeholder="0"
                  />
                  <p className="text-xs text-muted-foreground">
                    All opportunities will be created in <strong>Prospecting</strong> stage
                  </p>
                </div>
              )}
              
              {bulkActionType === 'assign-owner' && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">New Owner</Label>
                  <Select value={bulkOwnerId} onValueChange={setBulkOwnerId}>
                    <SelectTrigger className="rounded-sm">
                      <SelectValue placeholder="Select owner" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {bulkActionType === 'add-tag' && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Tag to Add</Label>
                  <Select value={bulkTag} onValueChange={setBulkTag}>
                    <SelectTrigger className="rounded-sm">
                      <SelectValue placeholder="Select tag" />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTACT_TYPE_TAGS.map((tag) => (
                        <SelectItem key={tag} value={tag}>
                          {tag}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {bulkActionType === 'update-followup' && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Next Follow-Up Date</Label>
                  <Input
                    type="date"
                    value={bulkFollowUp}
                    onChange={(e) => setBulkFollowUp(e.target.value)}
                    className="rounded-sm"
                  />
                </div>
              )}
              
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setBulkActionDialogOpen(false)} className="rounded-sm">
                  Cancel
                </Button>
                <Button onClick={handleBulkAction} className="rounded-sm" disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Floating Bulk Action Bar — appears when one or more contacts selected */}
        {selectedContacts.length > 0 && (
          <div
            className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-2xl"
            data-testid="bulk-action-bar"
          >
            <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-sm border bg-background shadow-2xl">
              <div className="flex items-center gap-2">
                <Badge className="rounded-sm" style={{ backgroundColor: '#B89B5E', color: 'white' }}>
                  {selectedContacts.length}
                </Badge>
                <span className="text-sm font-medium">
                  selected
                </span>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setBulkComposerOpen(true)}
                  className="rounded-sm h-8"
                  data-testid="bulk-action-send-email"
                >
                  <Send className="h-3.5 w-3.5 mr-1.5" /> Send email
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setBulkActionDialogOpen(true)}
                  className="rounded-sm h-8"
                  data-testid="bulk-action-add-tag"
                >
                  <Tag className="h-3.5 w-3.5 mr-1.5" /> Add tag
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedContacts([])}
                  className="rounded-sm h-8 w-8 p-0"
                  title="Clear selection"
                  data-testid="bulk-action-clear"
                >
                  <XIcon className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Mass Email Composer */}
        {bulkComposerOpen && (() => {
          const selected = contacts.filter((c) => selectedContacts.includes(c.id) && c.email);
          if (selected.length === 0) {
            toast.error('None of the selected contacts have an email on file');
            setBulkComposerOpen(false);
            return null;
          }
          return (
            <MassEmailComposer
              open={bulkComposerOpen}
              onOpenChange={setBulkComposerOpen}
              initialSubject="Quick note for {first_name}"
              initialBody="Hi {first_name},<br><br>"
              prefilledRecipients={selected.map((c) => ({
                email: c.email,
                name: `${c.first_name || ''} ${c.last_name || ''}`.trim(),
                company: c.account_name || '',
                contact_title: c.title || '',
                contact_id: c.id,
              }))}
            />
          );
        })()}

        {/* Single-row email composer (per-row Mail icon) */}
        {singleComposer && (
          <MassEmailComposer
            open={!!singleComposer}
            onOpenChange={(o) => { if (!o) setSingleComposer(null); }}
            initialSubject="Quick note for {first_name}"
            initialBody="Hi {first_name},<br><br>"
            lockedRecipient={{
              email: singleComposer.email,
              name: `${singleComposer.first_name || ''} ${singleComposer.last_name || ''}`.trim(),
              company: singleComposer.account_name || '',
              contact_title: singleComposer.title || '',
              contact_id: singleComposer.id,
            }}
          />
        )}
      </div>
    </Layout>
  );
}
